import type { Product, Category } from '~/types/shopware'
import type { SeoUrl } from '~/lib/utils/seo'
import type { Ref } from 'vue'
import { pickCanonicalSeoUrl } from '~/lib/utils'

export const useSeoUrls = () => {
  const localePath = useLocalePath()
  const seoStore = useSeoUrlStore()
  const { getCurrentShopLanguage } = useShopLanguages()

  const languageId = computed(() => getCurrentShopLanguage())

  const getRawProductPath = (product: Product): string => {
    const path = pickCanonicalSeoUrl(product?.seoUrls as SeoUrl[] | undefined, { languageId: languageId.value })
    if (path) return path
    return seoStore.getProductUrlById(product.id) || `/shop/detail/${product.id}`
  }

  const getRawCategoryPath = (category: Category): string => {
    const path = pickCanonicalSeoUrl(category?.seoUrls as SeoUrl[] | undefined, { languageId: languageId.value })
    if (path) return path
    return seoStore.getCategoryUrlById(category.id) || `/shop/navigation/${category.id}`
  }

  const useProductSeoPath = (product: Ref<Product>) => {
    const prodRef = product
    watchEffect(() => {
      const p = prodRef.value as Product
      if (p && !p.seoUrls?.length && p.id) seoStore.ensureProductUrls([p.id])
    })
    return computed(() => localePath(getRawProductPath(prodRef.value as Product)))
  }

  const useProductIdSeoPath = (productId: Ref<string | undefined>) => {
    const idRef = productId
    watchEffect(() => {
      const id = idRef.value as string | undefined
      if (id) seoStore.ensureProductUrls([id])
    })
    return computed<string | undefined>(() => {
      const id = idRef.value as string | undefined
      if (!id) return undefined
      const path = seoStore.getProductUrlById(id) || `/shop/detail/${id}`
      return localePath(path)
    })
  }

  const useCategorySeoPath = (category: Ref<Category>) => {
    const catRef = category
    watchEffect(() => {
      const c = catRef.value as Category
      if (c && !c.seoUrls?.length && c.id) seoStore.ensureCategoryUrls([c.id])
    })
    return computed(() => localePath(getRawCategoryPath(catRef.value as Category)))
  }

  return {
    // raw builders (non-localized route path)
    getRawProductPath,
    getRawCategoryPath,
    // localized NuxtLink paths
    useProductSeoPath,
    useProductIdSeoPath,
    useCategorySeoPath,
  }
}
